/**
 * Cart Hooks
 *
 * TanStack Query hooks for cart operations.
 * These hooks provide caching, optimistic updates, and automatic refetching.
 */

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { AxiosError } from 'axios';
import { useCartStore } from '@/stores/useCartStore';
import { toast } from '@/stores/useToastStore';
import * as cartApi from '@/api/cart';
import * as pricingApi from '@/api/pricing';
import type { CartValidationRequest, CartValidationResult } from '@/api/cart';
import {
    CustomerCartResponse,
    CustomerAddToCartRequest,
    PricingCalculationRequest,
    CartItemPricingRequest,
} from '@/types/cart';
import type { PriceValidationRequest } from '@/types';

// =============================================================================
// Query Keys
// =============================================================================

export const cartKeys = {
    all: ['cart'] as const,
    cart: () => [...cartKeys.all, 'current'] as const,
    pricing: (request: PricingCalculationRequest) =>
        [...cartKeys.all, 'pricing', request] as const,
    itemPricing: (request: CartItemPricingRequest) =>
        [...cartKeys.all, 'itemPricing', request] as const,
    validation: () => [...cartKeys.all, 'validation'] as const,
    priceValidation: () => [...cartKeys.all, 'priceValidation'] as const,
};

// =============================================================================
// Cart Query Hook
// =============================================================================

/**
 * Hook to fetch the current cart
 */
export const useCart = () => {
    const { setCartFromApi, setLoading, setError } = useCartStore();

    return useQuery({
        queryKey: cartKeys.cart(),
        queryFn: async () => {
            setLoading(true);
            try {
                const cart = await cartApi.getCart();
                setCartFromApi(cart);
                return cart;
            } catch (error) {
                setError('Failed to load cart');
                throw error;
            } finally {
                setLoading(false);
            }
        },
        staleTime: 30 * 1000, // 30 seconds
        gcTime: 5 * 60 * 1000, // 5 minutes
    });
};

// =============================================================================
// Cart Mutation Hooks
// =============================================================================

/**
 * Hook to add item to cart
 */
export const useAddToCart = () => {
    const queryClient = useQueryClient();
    const { openDrawer, setError, setCartFromApi, setLastAddedItem } = useCartStore();

    return useMutation({
        mutationFn: (request: CustomerAddToCartRequest) => cartApi.addToCart(request),
        onSuccess: (cart) => {
            // Capture current item IDs before syncing to find the newly added one
            const currentItemIds = new Set(useCartStore.getState().items.map((i) => i.id));
            const newApiItem = cart.items?.find((i) => i.id && !currentItemIds.has(i.id));

            // Update the query cache
            queryClient.setQueryData(cartKeys.cart(), cart);
            // Update Zustand store (single source of truth sync)
            setCartFromApi(cart);

            // On mobile: show a lightweight snackbar instead of opening the full drawer
            const isMobile =
                typeof window !== 'undefined' &&
                window.matchMedia('(max-width: 1023px)').matches;

            if (isMobile) {
                setLastAddedItem({
                    name: newApiItem?.equipmentName || 'Item',
                    image: newApiItem?.imageUrl || undefined,
                });
            } else {
                // Desktop: open the cart drawer as before
                openDrawer();
            }
        },
        onError: (error: Error) => {
            const axiosError = error as AxiosError<{
                errorCode?: string;
                message?: string;
                details?: {
                    equipmentTypeId?: string;
                    equipmentTypeName?: string;
                    locationId?: string;
                    locationName?: string;
                    requested?: number;
                    available?: number;
                };
            }>;

            const status = axiosError.response?.status;
            const errorData = axiosError.response?.data;

            if (status === 409 && errorData?.errorCode) {
                switch (errorData.errorCode) {
                    case 'CART_101':
                        toast.error(
                            `Only ${errorData.details?.available ?? 0} available for the selected dates. You requested ${errorData.details?.requested ?? '?'}.`
                        );
                        break;
                    case 'CART_104':
                        toast.error('Your cart is for another destination. Open your cart to review it or start a new booking.');
                        break;
                    case 'CART_103':
                        toast.error(
                            `${errorData.details?.equipmentTypeName ?? 'This item'} is not available at ${errorData.details?.locationName ?? 'the selected location'}.`
                        );
                        break;
                    default:
                        toast.error(errorData.message ?? 'Unable to add item to cart.');
                }
            } else if (status === 404) {
                toast.error('Product not found. It may no longer be available.');
            } else {
                toast.error('We couldn\'t add this item to your cart. Please try again.');
            }

            setError(errorData?.message || error.message || 'Failed to add item to cart');
        },
    });
};

/**
 * Hook to update cart item quantity
 */
export const useUpdateCartItem = () => {
    const queryClient = useQueryClient();
    const { updateItemQuantity, setError, setCartFromApi } = useCartStore();

    return useMutation({
        mutationFn: ({
            itemId,
            quantity,
        }: {
            itemId: string;
            quantity: number;
        }) => cartApi.updateCartItem(itemId, quantity),
        onMutate: async ({ itemId, quantity }) => {
            // Cancel outgoing refetches
            await queryClient.cancelQueries({ queryKey: cartKeys.cart() });
            // Snapshot previous value
            const previousCart = queryClient.getQueryData<CustomerCartResponse>(
                cartKeys.cart()
            );
            // Optimistic update
            updateItemQuantity(itemId, quantity);
            return { previousCart };
        },
        onSuccess: (cart) => {
            queryClient.setQueryData(cartKeys.cart(), cart);
            // Sync backend response to Zustand store (single source of truth)
            setCartFromApi(cart);
        },
        onError: (error: Error, _, context) => {
            // Rollback on error
            if (context?.previousCart) {
                queryClient.setQueryData(cartKeys.cart(), context.previousCart);
                // Also rollback Zustand store
                setCartFromApi(context.previousCart);
            }
            setError(error.message || 'Failed to update cart item');
        },
    });
};

/**
 * Hook to remove cart item
 */
export const useRemoveCartItem = () => {
    const queryClient = useQueryClient();
    const { removeItem, setError, setCartFromApi } = useCartStore();

    return useMutation({
        mutationFn: (itemId: string) => cartApi.removeCartItem(itemId),
        onMutate: async (itemId) => {
            await queryClient.cancelQueries({ queryKey: cartKeys.cart() });
            const previousCart = queryClient.getQueryData<CustomerCartResponse>(
                cartKeys.cart()
            );
            // Optimistic update
            removeItem(itemId);
            return { previousCart };
        },
        onSuccess: (cart) => {
            queryClient.setQueryData(cartKeys.cart(), cart);
            // Sync backend response to Zustand store (single source of truth)
            setCartFromApi(cart);
        },
        onError: (error: Error, _, context) => {
            if (context?.previousCart) {
                queryClient.setQueryData(cartKeys.cart(), context.previousCart);
                // Also rollback Zustand store
                setCartFromApi(context.previousCart);
            }
            setError(error.message || 'Failed to remove cart item');
        },
    });
};

/**
 * Hook to clear the entire cart
 */
export const useClearCart = () => {
    const queryClient = useQueryClient();
    const { clearCart, setError } = useCartStore();

    return useMutation({
        mutationFn: () => cartApi.clearCart(),
        onSuccess: () => {
            clearCart();
            queryClient.setQueryData(cartKeys.cart(), (old: CustomerCartResponse | undefined) => {
                if (!old) return null;
                return {
                    ...old,
                    items: [],
                    subtotal: 0,
                    totalAmount: 0,
                    depositAmount: 0,
                    discountAmount: 0,
                    itemCount: 0,
                };
            });
        },
        onError: (error: Error) => {
            setError(error.message || 'Failed to clear cart');
        },
    });
};

// =============================================================================
// Pricing Hooks
// =============================================================================

/**
 * Hook to calculate cart pricing
 */
export const useCartPricing = (request: PricingCalculationRequest) => {
    return useQuery({
        queryKey: cartKeys.pricing(request),
        queryFn: () => cartApi.calculateCartPricing(request),
        enabled: request.items.length > 0,
        staleTime: 60 * 1000, // 1 minute
    });
};

/**
 * Hook to calculate single item pricing (for PDP)
 */
export const useItemPricing = (
    request: CartItemPricingRequest,
    enabled = true
) => {
    return useQuery({
        queryKey: cartKeys.itemPricing(request),
        queryFn: () => cartApi.calculateItemPricing(request),
        enabled:
            enabled &&
            !!request.equipmentTypeId &&
            !!request.startDate &&
            !!request.endDate,
        staleTime: 60 * 1000,
    });
};

// =============================================================================
// Validation Hook
// =============================================================================

/**
 * Hook to validate cart for checkout
 * Optionally accepts validation request with date/location params
 */
export const useValidateCart = () => {
    const { setValidationErrors, setError } = useCartStore();

    return useMutation({
        mutationFn: (request?: CartValidationRequest) =>
            cartApi.validateCart(request),
        onSuccess: (result: CartValidationResult) => {
            if (!result.isValid) {
                // Map equipment item validation errors
                const equipmentErrors = result.items
                    .filter((item) => !item.canFulfill)
                    .map((item) => ({
                        itemId: item.itemId,
                        equipmentName: item.equipmentName,
                        error:
                            item.error ||
                            `Only ${item.availableQuantity} available`,
                        availableQuantity: item.availableQuantity,
                        requestedQuantity: item.requestedQuantity,
                        priceChanged: item.priceChanged,
                        newPrice: item.newUnitPrice,
                    }));

                // Map accessory item validation errors
                const accessoryErrors = result.accessoryItems
                    .filter((item) => !item.canFulfill)
                    .map((item) => ({
                        itemId: item.itemId,
                        equipmentName: item.accessoryName,
                        error:
                            item.error ||
                            `Only ${item.availableQuantity} available`,
                        availableQuantity: item.availableQuantity,
                        requestedQuantity: item.requestedQuantity,
                        priceChanged: item.priceChanged,
                        newPrice: item.newUnitPrice,
                        isAccessory: true,
                    }));

                setValidationErrors([...equipmentErrors, ...accessoryErrors]);
            } else {
                setValidationErrors([]);
            }
            return result;
        },
        onError: (error: Error) => {
            setError(error.message || 'Failed to validate cart');
        },
    });
};

// =============================================================================
// Price Validation Hook (for checkout)
// =============================================================================

/**
 * Hook to validate cart prices against current pricing before payment.
 * Used to detect price changes since cart was last calculated.
 *
 * Returns validation result with:
 * - isValid: true if all prices match current pricing
 * - Per-item comparison of old vs new prices
 * - Total differences for user display
 */
export const useValidateCartPricing = () => {
    const { setError } = useCartStore();

    return useMutation({
        mutationFn: (request: PriceValidationRequest) =>
            pricingApi.validateCartPricing(request),
        onError: (error: Error) => {
            setError(error.message || 'Failed to validate pricing');
        },
    });
};

// =============================================================================
// Cart Merge Hook (for login)
// =============================================================================

/**
 * Hook to transfer guest cart to user cart on login/register
 * Note: Function name kept as "useMergeCart" for backward compatibility
 */
export const useMergeCart = () => {
    const queryClient = useQueryClient();
    const { setError, setCartFromApi } = useCartStore();

    return useMutation({
        mutationFn: ({ guestSessionId }: { guestSessionId: string }) =>
            cartApi.mergeCart(guestSessionId),
        onSuccess: (cart) => {
            // Update the query cache
            queryClient.setQueryData(cartKeys.cart(), cart);
            // Sync to Zustand store (single source of truth sync)
            setCartFromApi(cart);
            queryClient.invalidateQueries({ queryKey: cartKeys.cart() });
        },
        onError: (error: Error) => {
            setError(error.message || 'Failed to transfer cart');
        },
    });
};
